import { formatMoney } from '@theme/money-formatting';
import {
  attributeClicks,
  campaignFromResponse,
  campaignProducts,
  loadPlacement,
  productHandle,
  productImage,
  sizedImage,
  storefrontPath,
} from '@theme/bt-nosto-placement';

const DEFAULT_MAX = 3;

/**
 * PDP 3-for-2 mix & match — Nosto JSON drawn into compact rows.
 * Placement comes from the section setting; Add still opens the shared
 * quick-add drawer.
 *
 * Last PDP caller of `loadPlacement`. If this list stops using the Nosto
 * JSON API (same change as get this look), delete `loadPlacement` and any
 * unused helpers from `@theme/bt-nosto-placement`.
 *
 * @extends {HTMLElement}
 */
class BtPdpMixMatch extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const addButton = target.closest('[data-mix-match-add]');
    if (!(addButton instanceof HTMLElement)) return;

    event.preventDefault();
    const item = addButton.closest('[data-mix-match-item]');
    if (!(item instanceof HTMLElement)) return;
    this.#openQuickAdd(item);
  };

  /**
   * @param {HTMLElement} item
   */
  async #openQuickAdd(item) {
    const productUrl = item.dataset.productUrl || '';
    if (!productUrl) return;

    const drawer = document.getElementById('quick-add-drawer');
    if (!drawer) return;
    if (typeof drawer.openForProduct !== 'function') {
      await customElements.whenDefined('quick-add-drawer');
    }
    if (typeof drawer.openForProduct === 'function') {
      await drawer.openForProduct(productUrl, null, { colour: item.dataset.productColour || '' });
    }
  }

  async #loadRecommendations() {
    const placementId = this.dataset.nostoPlacement?.trim();
    if (!placementId) {
      this.#onEmpty();
      return;
    }

    try {
      const response = await loadPlacement({
        placementId,
        productId: this.dataset.productId,
        logPrefix: '[bt-pdp-mix-match]',
      });
      const campaign = campaignFromResponse(response, placementId);
      const products = this.#productsFromCampaign(campaign);

      const items = products.map((product) => this.#itemFromJson(product)).filter(Boolean);
      const list = this.#list();
      if (!list || !items.length) {
        this.#onEmpty();
        return;
      }

      list.replaceChildren(...items);
      this.hidden = false;
      attributeClicks(list, campaign);
    } catch (error) {
      console.warn('[bt-pdp-mix-match] Nosto recommendations failed', error);
      this.#onEmpty();
    }
  }

  /**
   * @param {any} campaign
   * @returns {import('./bt-nosto-placement').NostoPlacementProduct[]}
   */
  #productsFromCampaign(campaign) {
    const limit = parseMax(this.dataset.maxProducts);
    const currentHandle = (this.dataset.productHandle || '').toLowerCase();
    const currentId = String(this.dataset.productId || '');

    return campaignProducts(campaign)
      .filter((product) => {
        const handle = productHandle(product).toLowerCase();
        const id = String(product.product_id || product.productId || '');
        if (currentId && id && id === currentId) return false;
        if (handle && handle === currentHandle) return false;
        return Boolean(handle || product.url || product.name || product.title);
      })
      .slice(0, limit);
  }

  /**
   * @param {import('./bt-nosto-placement').NostoPlacementProduct} product
   * @returns {HTMLElement | null}
   */
  #itemFromJson(product) {
    const template = this.querySelector('[data-mix-match-item-template]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const handle = productHandle(product);
    const url = storefrontPath(product.url || (handle ? `/products/${handle}` : ''));
    const title = String(product.name || product.title || handle).trim();
    if (!url || !title) return null;

    const node = template.content.firstElementChild?.cloneNode(true);
    if (!(node instanceof HTMLElement)) return null;

    node.dataset.productHandle = handle;
    node.dataset.productId = String(product.product_id || product.productId || '');
    node.dataset.productUrl = url;
    const colour = nostoColour(product);
    if (colour) node.dataset.productColour = colour;

    const media = node.querySelector('.bt-pdp-mix-match__media');
    if (media instanceof HTMLAnchorElement) media.href = url;

    const image = node.querySelector('.bt-pdp-mix-match__image');
    const imageUrl = sizedImage(productImage(product));
    if (image instanceof HTMLImageElement) {
      image.alt = title;
      if (imageUrl) {
        image.src = imageUrl;
      } else {
        image.remove();
      }
    }

    const titleLink = node.querySelector('[data-mix-match-title]');
    if (titleLink instanceof HTMLAnchorElement) {
      titleLink.href = url;
      titleLink.textContent = title;
    }

    const priceEl = node.querySelector('[data-mix-match-price]');
    if (priceEl) {
      try {
        priceEl.textContent = nostoPriceLabel(
          product,
          this.dataset.moneyFormat || '£{{amount}}',
          this.dataset.currency || 'GBP'
        );
      } catch {
        priceEl.textContent = String(product.price_text || product.priceText || product.price || '');
      }
    }

    const addButton = node.querySelector('[data-mix-match-add]');
    if (addButton instanceof HTMLButtonElement) {
      const addLabel = this.dataset.addLabel || addButton.textContent || 'add';
      addButton.setAttribute('aria-label', `${addLabel} ${title}`);
    }

    return node;
  }

  #onEmpty() {
    const list = this.#list();
    list?.replaceChildren();
    this.hidden = !this.hasAttribute('data-design-mode');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #list() {
    const list = this.querySelector('[data-mix-match-list]');
    return list instanceof HTMLElement ? list : null;
  }
}

/**
 * @param {string | undefined} value
 */
function parseMax(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX;
}

/**
 * Colour Nosto attached to the recommendation. Falls back to the
 * "in {colour}" suffix on the product name.
 * @param {import('./bt-nosto-placement').NostoPlacementProduct} product
 */
function nostoColour(product) {
  const fields = product.custom_fields || product.customFields;
  if (fields && typeof fields === 'object') {
    for (const [key, value] of Object.entries(fields)) {
      if (!/^(colou?r)$/i.test(key)) continue;
      const text = Array.isArray(value) ? value[0] : value;
      const colour = String(text || '')
        .split(':')
        .pop()
        ?.trim();
      if (colour) return colour;
    }
  }

  const name = String(product.name || product.title || '');
  const match = name.match(/\sin\s+(.+)$/i);
  return match ? match[1].trim() : '';
}

/**
 * @param {import('./bt-nosto-placement').NostoPlacementProduct} product
 * @param {string} moneyFormat
 * @param {string} currency
 */
function nostoPriceLabel(product, moneyFormat, currency) {
  const raw = product.price_text || product.priceText;
  if (typeof raw === 'string' && /[^\d.,\s-]/.test(raw.trim())) return raw.trim();

  const amount = Number(product.price);
  if (!Number.isFinite(amount)) return '';
  return formatMoney(toMinorUnits(amount, currency), moneyFormat, currency);
}

/**
 * Nosto rec prices are major units; formatMoney expects minor units.
 * @param {number} amount
 * @param {string} currency
 */
function toMinorUnits(amount, currency) {
  const precision = currency.toUpperCase() === 'JPY' ? 0 : 2;
  return Math.round(amount * 10 ** precision);
}

if (!customElements.get('bt-pdp-mix-match')) {
  customElements.define('bt-pdp-mix-match', BtPdpMixMatch);
}
